/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/BooleanSections.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-BOOLEAN-SECTIONS-001
 * PURPOSE: Renders attachment, calendar, read, and starred boolean filters.
 */
import { Calendar, Eye, EyeOff, Paperclip, Star } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import { SectionHeader } from './SidebarChrome'
import type { SectionProps, SidebarFilters } from './types'

interface FlagsSectionProps extends SectionProps {
  hasAttachments?: boolean
  hasCalendarEvent?: boolean
  onPatch: (patch: Partial<SidebarFilters>) => void
}

export function FlagsSection({
  expanded,
  onToggle,
  hasAttachments,
  hasCalendarEvent,
  onPatch,
}: FlagsSectionProps) {
  return (
    <div className="mb-3">
      <SectionHeader
        label="Other"
        expanded={expanded}
        onToggle={onToggle}
        count={
          (hasAttachments !== undefined ? 1 : 0) +
          (hasCalendarEvent !== undefined ? 1 : 0)
        }
      />
      {expanded && (
        <div className="space-y-0.5">
          <button
            onClick={() =>
              onPatch({
                hasAttachments:
                  hasAttachments === true ? undefined : true,
              })
            }
            className={cn(
              'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
              hasAttachments === true
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <Paperclip className="h-3 w-3" /> Has attachment
          </button>
          <button
            onClick={() =>
              onPatch({
                hasCalendarEvent:
                  hasCalendarEvent === true ? undefined : true,
              })
            }
            className={cn(
              'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
              hasCalendarEvent === true
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <Calendar className="h-3 w-3" /> Calendar invite
          </button>
        </div>
      )}
    </div>
  )
}

interface StatusSectionProps extends SectionProps {
  isRead?: boolean
  isStarred?: boolean
  onPatch: (patch: Partial<SidebarFilters>) => void
}

export function StatusSection({
  expanded,
  onToggle,
  isRead,
  isStarred,
  onPatch,
}: StatusSectionProps) {
  return (
    <div className="mb-3">
      <SectionHeader
        label="Status"
        expanded={expanded}
        onToggle={onToggle}
        count={
          (isRead !== undefined ? 1 : 0) + (isStarred ? 1 : 0)
        }
      />
      {expanded && (
        <div className="space-y-0.5">
          <button
            onClick={() => onPatch({ isRead: isRead === false ? undefined : false })}
            className={cn(
              'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
              isRead === false
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <EyeOff className="h-3 w-3" /> Unread
          </button>
          <button
            onClick={() => onPatch({ isRead: isRead === true ? undefined : true })}
            className={cn(
              'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
              isRead === true
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <Eye className="h-3 w-3" /> Read
          </button>
          <button
            onClick={() => onPatch({ isStarred: isStarred ? undefined : true })}
            className={cn(
              'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
              isStarred
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            <Star className="h-3 w-3" /> Starred
          </button>
        </div>
      )}
    </div>
  )
}
